<template>
  <div class="badge-style-preview-wrap" :style="outerStyle">
    <canvas
      ref="previewCanvas"
      class="badge-style-preview-canvas"
      :width="canvasWidth || 684"
      :height="canvasHeight || 366"
      :style="canvasDisplayStyle" />
  </div>
</template>

<script>
/**
 * 校徽 Canvas 效果预览：按图层 JSON 在底图上绘制照片、文字、二维码。
 * 最后更新：2026-05-27
 */
import { BaseApiUrl } from '../../api/base';
import {
  badgeCanvasWidth,
  badgeCanvasHeight,
  buildPreviewQrUrl,
  previewSamplePhotoUrl
} from './badgeStyleLayerDefaults'

export default {
  name: 'BadgeStylePreview',
  props: {
    canvasWidth: { type: Number, default: badgeCanvasWidth },
    canvasHeight: { type: Number, default: badgeCanvasHeight },
    backgroundUrl: { type: String, default: '' },
    layersJson: { type: String, default: '[]' },
    resolved: {
      type: Object,
      default: () => ({
        schoolName: '',
        className: '',
        studentName: '',
        badgeNo: '',
        photoUrl: '',
        moralQrUrl: ''
      })
    },
    maxWidth: { type: Number, default: badgeCanvasWidth }
  },
  data() {
    return {
      photoLoadFailed: false,
      renderToken: 0,
      imageCache: {}
    }
  },
  computed: {
    scale() {
      const w = this.canvasWidth || 684
      return Math.min(1, this.maxWidth / w)
    },
    canvasDisplayStyle() {
      const w = Math.ceil((this.canvasWidth || 684) * this.scale)
      const h = Math.ceil((this.canvasHeight || 366) * this.scale)
      return {
        width: w + 'px',
        height: h + 'px'
      }
    },
    outerStyle() {
      const w = Math.ceil((this.canvasWidth || 684) * this.scale) + 16
      const h = Math.ceil((this.canvasHeight || 366) * this.scale) + 16
      return { width: w + 'px', minHeight: h + 'px' }
    },
    layers() {
      if (!this.layersJson) return []
      try {
        return JSON.parse(this.layersJson)
      } catch (e) {
        return []
      }
    }
  },
  watch: {
    canvasWidth: 'scheduleRender',
    canvasHeight: 'scheduleRender',
    backgroundUrl: 'scheduleRender',
    layersJson: 'scheduleRender',
    resolved: {
      deep: true,
      handler: 'scheduleRender'
    }
  },
  mounted() {
    this.scheduleRender()
  },
  methods: {
    /** 防抖调度重绘（props 变更时合并为一次 render） */
    scheduleRender() {
      this.renderToken += 1
      const token = this.renderToken
      this.$nextTick(() => {
        if (token === this.renderToken) this.renderPreview(token)
      })
    },
    /** 解析静态资源 URL（开发环境 /badge-order 走代理，其余拼 API 根地址） */
    assetUrl(url) {
      if (!url) return ''
      const raw = url.split('?')[0]
      if (raw.indexOf('http://') === 0 || raw.indexOf('https://') === 0) return url
      if (raw.indexOf('/static/') === 0) return raw
      if (raw.indexOf('/badge-order/') === 0) {
        if (process.env.NODE_ENV === 'development') return url
        return this.$resolveImageSrc(raw)
      }
      return this.$resolveImageSrc(url.indexOf('/') === 0 ? url : '/' + url)
    },
    /** 加载图片并缓存，失败返回 null */
    loadImage(url) {
      const full = this.assetUrl(url)
      if (!full) return Promise.resolve(null)
      if (this.imageCache[full]) {
        return Promise.resolve(this.imageCache[full])
      }
      return new Promise(resolve => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          this.imageCache[full] = img
          resolve(img)
        }
        img.onerror = () => resolve(null)
        img.src = full
      })
    },
    /** 绘制圆角矩形路径（用于照片裁剪） */
    roundRectPath(ctx, x, y, w, h, r) {
      const radius = Math.min(r, w / 2, h / 2)
      ctx.beginPath()
      ctx.moveTo(x + radius, y)
      ctx.lineTo(x + w - radius, y)
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
      ctx.lineTo(x + w, y + h - radius)
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
      ctx.lineTo(x + radius, y + h)
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
      ctx.lineTo(x, y + radius)
      ctx.quadraticCurveTo(x, y, x + radius, y)
      ctx.closePath()
    },
    /** 主绘制流程：底图 + 各图层（照片/文字/二维码） */
    async renderPreview(token) {
      const canvas = this.$refs.previewCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const cw = this.canvasWidth || 684
      const ch = this.canvasHeight || 366
      ctx.clearRect(0, 0, cw, ch)

      const bg = await this.loadImage(this.backgroundUrl)
      if (token !== this.renderToken) return
      if (bg) {
        ctx.drawImage(bg, 0, 0, cw, ch)
      } else {
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, cw, ch)
      }

      for (const layer of this.layers) {
        if (!layer || !layer.type) continue
        if (layer.hidden) continue
        if (layer.type === 'image' && layer.key === 'photo') {
          await this.drawPhoto(ctx, layer, token)
        } else if (layer.type === 'text') {
          this.drawText(ctx, layer)
        } else if (layer.type === 'qrcode') {
          await this.drawQr(ctx, layer, token)
        }
      }
    },
    /** 绘制学生照片图层（cover 裁剪 + 圆角） */
    async drawPhoto(ctx, layer, token) {
      const url = (this.resolved.photoUrl || '').trim()
      const src = url || previewSamplePhotoUrl
      const img = await this.loadImage(src)
      if (token !== this.renderToken || !img) return

      const x = layer.x || 0
      const y = layer.y || 0
      const w = layer.w || 80
      const h = layer.h || 100
      const r = layer.borderRadius || 0

      ctx.save()
      if (r > 0) {
        this.roundRectPath(ctx, x, y, w, h, r)
        ctx.clip()
      }
      this.drawCoverImage(ctx, img, x, y, w, h)
      ctx.restore()
    },
    /** 等比 cover 方式将图片绘制到指定矩形 */
    drawCoverImage(ctx, img, x, y, w, h) {
      const imageRatio = img.width / img.height
      const boxRatio = w / h
      let sx = 0
      let sy = 0
      let sw = img.width
      let sh = img.height
      if (imageRatio > boxRatio) {
        sw = img.height * boxRatio
        sx = (img.width - sw) / 2
      } else if (imageRatio < boxRatio) {
        sh = img.width / boxRatio
        sy = (img.height - sh) / 2
      }
      ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h)
    },
    /** 绘制文字图层（校名/姓名/编号） */
    drawText(ctx, layer) {
      const key = layer.key
      const map = {
        schoolName: this.resolved.schoolName || '学校名称',
        className: this.resolved.className || '班级',
        studentName: this.resolved.studentName || '学生姓名',
        badgeNo: this.resolved.badgeNo || '编号'
      }
      const text = map[key] || ''
      if (!text) return

      const x = layer.x || 0
      const y = layer.y || 0
      const fontSize = layer.fontSize || 14
      const fontWeight = layer.fontWeight || 'normal'
      const fontFamily = '"Microsoft YaHei", "PingFang SC", sans-serif'

      ctx.save()
      ctx.font = fontWeight + ' ' + fontSize + 'px ' + fontFamily
      ctx.fillStyle = layer.color || '#303133'
      ctx.textBaseline = 'top'
      ctx.textAlign = 'left'
      ctx.fillText(text, x, y)
      ctx.restore()
    },
    /** 绘制德育二维码图层（无 URL 时用编号生成预览码） */
    async drawQr(ctx, layer, token) {
      const size = layer.size || 80
      const x = layer.x || 0
      const y = layer.y || 0
      let url = this.resolved.moralQrUrl
      if (!url) url = buildPreviewQrUrl(this.resolved.badgeNo, size)
      const img = await this.loadImage(url)
      if (token !== this.renderToken) return
      if (img) {
        ctx.drawImage(img, x, y, size, size)
      } else {
        ctx.strokeStyle = '#b5cfe8'
        ctx.strokeRect(x, y, size, size)
      }
    }
  }
}
</script>

<style scoped>
.badge-style-preview-wrap {
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #f5f7fa;
  padding: 8px;
}
.badge-style-preview-canvas {
  display: block;
  background: #fff;
}
</style>
